import React, { useEffect, useMemo } from "react";
import { Alert, Empty, Form, Modal, Select, Space, Tag, Typography } from "antd";
import AircraftSelector from "../../../../../components/common/AircraftSelector";
import { getScenarioStrategyLabel } from "../../lib/agentTrackUtils";
import { getDefaultEditingTrack, getDraftFlights } from "../../lib/trackUtils";
import useScheduleDraftAgentStrategies from "../../hooks/useScheduleDraftAgentStrategies";

const { Paragraph, Text } = Typography;

function getDraftAircraftRegs(draft) {
    const scopeRegs = Array.isArray(draft?.aircraft_scope) ? draft.aircraft_scope : [];
    const flightRegs = Array.from(new Set(getDraftFlights(draft).map((flight) => flight?.aircraft_reg).filter(Boolean)));
    return scopeRegs.length ? scopeRegs : flightRegs;
}

function buildAircraftSelectorData(regs) {
    return regs.map((registration) => ({ registration }));
}

const AgentTriggerDialog = ({ open, draft, currentEditingTrackId, onCancel, onTriggerAgent, confirmLoading = false }) => {
    const [form] = Form.useForm();
    const strategiesQuery = useScheduleDraftAgentStrategies({ enabled: open });
    const sourceTrackId = currentEditingTrackId || getDefaultEditingTrack(draft)?.id || draft?.default_track_id;
    const strategies = useMemo(() => {
        const data = strategiesQuery.data;
        if (Array.isArray(data)) {
            return data;
        }
        return data?.items || [];
    }, [strategiesQuery.data]);
    const aircraftRegs = useMemo(() => getDraftAircraftRegs(draft), [draft]);
    const aircraftSelectorData = useMemo(() => buildAircraftSelectorData(aircraftRegs), [aircraftRegs]);
    const aircraftOptionKey = aircraftRegs.join("|");
    const selectedAircraftReg = Form.useWatch("aircraft_reg", form);
    const strategyPromptIds = Form.useWatch("strategy_prompt_ids", form) || [];
    const selectedStrategies = useMemo(() => {
        const selectedStrategyIdSet = new Set(strategyPromptIds.map((id) => String(id)));
        return strategies.filter((strategy) => selectedStrategyIdSet.has(String(strategy.id)));
    }, [strategies, strategyPromptIds]);

    useEffect(() => {
        if (!open) {
            return;
        }

        form.setFieldsValue({ strategy_prompt_ids: [] });
    }, [form, open]);

    useEffect(() => {
        if (!open) {
            return;
        }

        const currentAircraftReg = form.getFieldValue("aircraft_reg");
        form.setFieldsValue({
            aircraft_reg: currentAircraftReg && aircraftRegs.includes(currentAircraftReg) ? currentAircraftReg : aircraftRegs[0],
        });
    }, [aircraftOptionKey, aircraftRegs, form, open]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            await onTriggerAgent?.({
                strategy_prompt_ids: values.strategy_prompt_ids,
                source_track_id: sourceTrackId,
                aircraft_reg: values.aircraft_reg,
                constraint_overrides: [],
            });
            form.resetFields();
            onCancel?.();
        } catch (error) {
            if (error?.errorFields) {
                return;
            }
        }
    };

    return (
        <Modal
            title="AI 智能编排"
            open={open}
            onCancel={onCancel}
            onOk={handleSubmit}
            okText="开始编排"
            cancelText="取消"
            confirmLoading={confirmLoading}
            okButtonProps={{ disabled: !strategies.length || !sourceTrackId }}
            destroyOnClose
        >
            <Space direction="vertical" size={14} style={{ width: "100%" }}>
                <Alert type="info" showIcon message="Agent 会按策略并行生成方案轨；执行期间当前航班计划会进入只读锁定。" />
                {strategiesQuery.error ? <Alert type="error" showIcon message="加载策略失败" description={strategiesQuery.error.message || "未知错误"} /> : null}

                <Form form={form} layout="vertical">
                    <Form.Item name="strategy_prompt_ids" label="编排策略" initialValue={[]} rules={[{ type: "array", min: 1, message: "至少选择一个策略" }]}>
                        <Select
                            mode="multiple"
                            showSearch
                            placeholder="选择策略提示词"
                            loading={strategiesQuery.isLoading || strategiesQuery.isFetching}
                            optionFilterProp="label"
                            options={strategies.map((strategy) => ({
                                value: strategy.id,
                                label: `${getScenarioStrategyLabel(strategy)} · #${strategy.id}`,
                            }))}
                            notFoundContent={<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无可用策略" />}
                        />
                    </Form.Item>

                    {selectedStrategies.length ? (
                        <div className="schedule-draft-agent-strategy-preview">
                            <Space wrap size={[6, 6]}>
                                {selectedStrategies.map((strategy) => (
                                    <Tag key={strategy.id} color="purple">{getScenarioStrategyLabel(strategy)}</Tag>
                                ))}
                            </Space>
                            {selectedStrategies.map((strategy) => (
                                <Paragraph key={strategy.id} ellipsis={{ rows: 4, expandable: true, symbol: "展开" }} className="schedule-draft-agent-strategy-text">
                                    {strategy.prompt_text || strategy.description || "该策略暂无提示词预览。"}
                                </Paragraph>
                            ))}
                        </div>
                    ) : null}

                    <Form.Item name="aircraft_reg" label="参与飞机" rules={[{ required: true, message: "至少选择一架飞机" }]}>
                        {aircraftSelectorData.length ? (
                            <AircraftSelector
                                tiled
                                tiledColumns={4}
                                valueType="registration"
                                displayMode="detailed"
                                aircrafts={aircraftSelectorData}
                                placeholder="选择参与飞机"
                            />
                        ) : (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="当前航班计划没有可用飞机范围" />
                        )}
                    </Form.Item>
                </Form>

                <div className="schedule-draft-agent-fanout-preview">
                    <Text strong>{`${strategyPromptIds.length || 0} 个策略 × ${selectedAircraftReg ? 1 : 0} 架飞机 = ${selectedAircraftReg ? strategyPromptIds.length || 0 : 0} 个 Agent 将并行执行`}</Text>
                </div>
            </Space>
        </Modal>
    );
};

export default AgentTriggerDialog;
